<template>
  <HeadTitle
    title="Configuraciones de cartas"
    description="Lista de las configuraciones de cartas y certificaciones creadas en el sistema."
  />
  <Divider />
  <div class="flex flex-row">
    <h2 class="font-normal">Cartas internas</h2>
    <InfoButton
      message="Aquí se listan las configuraciones de cartas internas que se han creado en el sistema."
    />
  </div>
  <Toolset
    ref="toolset"
    :searcher="fetchInternalLetters"
    add-button-icon="pi pi-plus"
    add-button-label="Crear nueva carta"
    :to-add="toAdd"
    class="mt-5"
  >
    <Button
      class="text-sm font-light mr-3"
      label="Reporte de cartas generadas"
      severity="secondary"
      icon=""
      @click="router.push('/reports/letters')"
    />
  </Toolset>
  <DataTable
    class="tabla mb-5 mt-3"
    :value="letterConfigs.data"
    paginator
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    removableSort
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="letterConfigs.totalItems"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
    sortField="updatedAt"
    :sortOrder="-1"
  >
    <template #empty> No se encontraron cartas.</template>
    <Column field="name" header="Nombre" sortable></Column>
    <Column field="description" header="Descripción" sortable></Column>
    <Column field="generationType" header="Tipo de carta" sortable> </Column>
    <Column field="payrolls" header="Nóminas" sortable>
      <template #body="{ data, field }">
        <span class="font-semibold text-overflow-elipsis">{{
          payrollsToString(data.payrolls)
        }}</span>
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        <span>{{ formatDateWithHour(data[field]) }}</span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            v-tooltip.bottom="'Ver detalles'"
            class="action-btn view p-button-sm mr-2"
            icon="pi pi-eye"
            @click="goToDetails(data['id'], data['generationType'])"
          />
          <Button
            v-tooltip.bottom="'Eliminar'"
            class="action-btn view p-button-sm px-4 bg-red-500 border-red-500"
            @click="deleteLetterConfig($event, data['id'])"
          >
            <i class="pi pi-trash text-white"></i>
          </Button>
        </div>
      </template>
    </Column>
  </DataTable>
  <Divider />
  <div class="flex flex-row">
    <h2 class="font-normal">Cartas externas</h2>
    <InfoButton
      message="Aquí se listan las configuraciones de cartas externas precargadas en el sistema."
    />
  </div>
  <ExternalLettersSection
    :letter-configs="externalLetterConfigs"
    :current-page-report-template="currentPageReportTemplateExt"
    :payrolls-to-string="payrollsToString"
    :format-date-with-hour="formatDateWithHour"
    @search="handleExternalSearch"
    @add="addExternalLetter"
    @view="({ id, generationType }) => goToDetails(id, generationType)"
    @delete="({ event, id }) => deleteLetterConfig(event, id)"
  />
  <Footer>
    <template #left>
      <AuditButton
        :entities="['LetterCertifConfig']"
        :root-id="company?.id"
        only-deleted
        label="Ver registros eliminados"
        :disabled="!company?.id"
      />
    </template>
  </Footer>
</template>
<script lang="ts" setup>
  import { computed, onMounted, reactive, ref } from 'vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import Footer from '@/shared/components/Footer.vue'
  import LetterConfigList from '../models/LetterConfig'
  import {
    getLettersConfig,
    deleteLetterConfigById,
  } from '../services/LetterConfgServices'
  import useLoading from '@/store/load'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import router from '@/router'
  import List from '@/shared/dtos/List'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import Payroll from '@/modules/payrolls-config/models/Payroll'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'
  import Divider from 'primevue/divider'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import ExternalLettersSection from '../components/ExternalLettersSection.vue'

  const loadingStore = useLoading()
  const companyStore = useCompany()
  const notificationStore = useNotifications()
  const confirm = useConfirm()

  const { company } = storeToRefs(companyStore)

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    await fetchData()
  })

  const letterConfigs = reactive<List<LetterConfigList[]>>({
    data: [],
    totalItems: 0,
  })

  const externalLetterConfigs = reactive<List<LetterConfigList[]>>({
    data: [],
    totalItems: 0,
  })

  const toolset = ref()
  const externalSearch = ref('')

  onMounted(async () => {
    await fetchData()
  })

  const toAdd = () => {
    router.push('/letterConfig/add')
  }

  const addExternalLetter = () => {
    router.push('/letterExternals/create')
  }

  const handleExternalSearch = async (searchTerm: string) => {
    externalSearch.value = searchTerm
    await fetchExternalLetters()
  }

  const goToDetails = (id: string, generationType: string) => {
    if (generationType === 'Externa')
      router.push(`/letterExternals/detail/${id}`)
    else router.push(`/letterConfig/detail/${id}`)
  }

  const deleteLetterConfig = (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message:
        '¿Estás seguro de que deseas eliminar esta configuración de carta?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        loadingStore.setLoadingState()
        const response = await deleteLetterConfigById(id)
        if (response === 'OK') {
          new Promise((resolve, reject) => {
            return resolve(
              notificationStore.addSuccesNotification({
                detail:
                  'La configuración de carta ha sido eliminada correctamente.',
                severity: 'success',
                summary: 'Eliminación exitosa',
              })
            )
          }).then(async () => {
            await fetchData()
          })
        }
        loadingStore.dismissLoadingState()
      },
    })
  }

  const fetchData = async () => {
    loadingStore.setLoadingState()
    await fetchInternalLetters()
    await fetchExternalLetters()
    loadingStore.dismissLoadingState()
  }

  const fetchInternalLetters = async () => {
    const reponse = await getLettersConfig({
      companyId: company.value?.id,
      pagination: {
        search: toolset.value.search,
      },
      isExternal: false,
    })

    letterConfigs.data = reponse.data.map((letter) => {
      return {
        ...letter,
        generationType: tranlateApprovalType(letter.generationType) || '',
      }
    })
    letterConfigs.totalItems = reponse.totalItems
  }

  const fetchExternalLetters = async () => {
    const reponse = await getLettersConfig({
      companyId: company.value?.id,
      pagination: {
        search: externalSearch.value,
      },
      isExternal: true,
    })

    externalLetterConfigs.data = reponse.data.map((letter) => {
      return {
        ...letter,
        generationType: tranlateApprovalType(letter.generationType) || '',
      }
    })
    externalLetterConfigs.totalItems = reponse.totalItems
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${letterConfigs.totalItems}`
  })

  const currentPageReportTemplateExt = computed(() => {
    return `{first} a {last} de ${externalLetterConfigs.totalItems}`
  })

  const tranlateApprovalType = (approvalType: string) => {
    switch (approvalType) {
      case 'WITH_APPROVAL':
        return 'Requiere aprobación'
      case 'WITH_MULTIPLE_APPROVALS':
        return 'Multiples aprobadores'
      case 'AUTOMATIC':
        return 'Automática'
      case 'EXTERNAL':
        return 'Externa'
    }
  }

  const payrollsToString = (payrolls: Payroll[]) => {
    return payrolls.map((p) => p.code + '-' + p.name).join(', ')
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
